import qs from "query-string";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import { useEmailModal } from "../store/useEmailModal";

export function useEmailModalController() {
  const reachableContactRepository = useService("repositories/reachable-contact-repository");
  const emailTemplateRepository = useService("repositories/email-template-repository");
  const enumRepository = useService("repositories/enum-repository");
  const emailRepository = useService("repositories/email-repository");

  const { setCloseEmailModal, caseData, modalTitle, setLastEmailReciepient } = useEmailModal();
  const { id } = useParams();
  const caseId = id || caseData?.id || caseData?.pk;

  const [recipientOpts, setRecipientOpts] = useState([]);
  const [formData, setFormData] = useState({
    recipients: [],
    emailBody: "",
  });

  const [requiredPlaceholders, setRequiredPlaceholders] = useState([]);
  const [templateId, setTemplateId] = useState(null);

  const [loading, setLoading] = useState({
    recipients: false,
    template: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);

  /* ---------------- API calls ---------------- */

  const getRecipientOpts = useCallback(async () => {
    if (!caseId) {
      return;
    }

    setLoading((p) => ({ ...p, recipients: true }));
    setErrorMessage([]);

    try {
      const resp = await reachableContactRepository.query({
        urlPath: `case/${caseId}/reachable-contacts/`,
      });
      setRecipientOpts(resp);
    } catch (error) {
      setErrorMessage(ApiRequestErrorHandler(error ?? []));
      setOpenError(true);
      setRecipientOpts([]);
    } finally {
      setLoading((p) => ({ ...p, recipients: false }));
    }
  }, [caseId, reachableContactRepository]);

  const getTemplate = useCallback(
    async (id) => {
      if (!caseId) {
        return;
      }

      setLoading((p) => ({ ...p, template: true }));
      setErrorMessage([]);

      try {
        setTemplateId(id);
        const resp = await emailTemplateRepository.query({
          urlPath: `case/${caseId}/preview_email/?${qs.stringify({
            template_id: id,
          })}`,
        });

        setFormData((p) => ({
          ...p,
          emailBody: resp?.htmlVersion || "",
        }));

        setRequiredPlaceholders(Array.isArray(resp?.required) ? resp.required : []);
      } catch (error) {
        setErrorMessage(ApiRequestErrorHandler(error?.response?.data || error));
        setOpenError(true);
      } finally {
        setLoading((p) => ({ ...p, template: false }));
      }
    },
    [caseId, emailTemplateRepository]
  );

  const getTemplateId = useCallback(async () => {
    setLoading((p) => ({ ...p, template: true }));
    setErrorMessage([]);

    try {
      const resp = await enumRepository.query({
        urlPath: `core/dynamic-options/email_templates/?${qs.stringify({
          name: "Final Review Completed",
        })}`,
      });

      if (resp?.length === 1) {
        getTemplate(resp[0].key);
      }
    } catch (error) {
      setErrorMessage(ApiRequestErrorHandler(error?.response?.data || error));
      setOpenError(true);
    } finally {
      setLoading((p) => ({ ...p, template: false }));
    }
  }, [enumRepository, getTemplate]);

  useEffect(() => {
    if (!caseId) {
      return;
    }
    getRecipientOpts();
    getTemplateId();
  }, [caseId, getRecipientOpts, getTemplateId]);

  const handleSend = async () => {
    if (!formData.recipients.length) {
      setErrorMessage(["Please select at least one recipient."]);
      setOpenError(true);
      return;
    }

    if (!validateRequiredPlaceholders()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await emailTemplateRepository.post(
        {
          recipients: formData.recipients.filter(Boolean),
          body: formData.emailBody,
          template_id: templateId,
        },
        { urlPath: `case/${caseId}/send_email/` }
      );
      setOpenSuccess(true);
      fetchEmailHistory();
    } catch (error) {
      setErrorMessage(ApiRequestErrorHandler(error?.response?.data || error));
      setOpenError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchEmailHistory = useCallback(async () => {
    try {
      const response = await emailRepository.query({
        urlPath: `case/${caseId}/emails/`,
      });
      if (response) {
        setLastEmailReciepient(response[0]);
      }
    } catch (error) {
      setErrorMessage(ApiRequestErrorHandler(error?.response?.data || error));
      setOpenError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [caseId, handleSend, openSuccess]);

  const validateRequiredPlaceholders = () => {
    if (!requiredPlaceholders.length) {
      return true;
    }

    const missing = requiredPlaceholders.filter((p) => !formData.emailBody.includes(p));

    if (missing.length) {
      setErrorMessage([
        `Content must include: ${missing.join(", ")}`,
        "Add the missing placeholders or refresh the default template.",
      ]);
      setOpenError(true);
      return false;
    }
    return true;
  };

  const handleClose = async () => {
    setFormData({ recipients: [], emailBody: "" });
    setErrorMessage([]);
    setCloseEmailModal();
  };

  const isSendDisabled =
    !formData.recipients.length || isSubmitting || loading.template || !formData.emailBody.trim();

  return {
    caseData,
    modalTitle,
    recipientOpts,
    formData,
    loading,
    isSubmitting,
    openSuccess,
    openError,
    errorMessage,
    isSendDisabled,

    setFormData,
    setOpenSuccess,
    setOpenError,

    handleSend,
    handleClose,
    fetchEmailHistory,
  };
}
